import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSession } from "@/hooks/useSession";
import { Button } from "@/components/ui/button";
import { Send, Link, X, Check, CheckCircle2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useProfile } from "@/hooks/useProfile";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LinkingGame as LinkingGameType, LinkingItem } from "./types";
import { useTrainingProgress } from "@/hooks/useTrainingProgress";

interface LinkingGameProps {
  fileId: string;
  resourceId: string;
  onComplete?: () => void;
  isPreview?: boolean;
}

export function LinkingGame({ fileId, resourceId, onComplete, isPreview }: LinkingGameProps) {
  const [gameData, setGameData] = useState<LinkingGameType | null>(null);
  const [items, setItems] = useState<LinkingItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selections, setSelections] = useState<{[key: string]: string}>({});
  const [results, setResults] = useState<{[key: string]: boolean}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingScore, setIsUpdatingScore] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [passingScore, setPassingScore] = useState<number>(70);
  const [attemptResult, setAttemptResult] = useState<{score: number, passed: boolean, completed: boolean} | null>(null);
  const { session } = useSession();
  const { toast } = useToast();
  const { data: profile } = useProfile(session?.user?.id);
  const isAdmin = profile?.role === 'admin';
  const { markAsCompleted } = useTrainingProgress(resourceId, fileId);
  const isTrainee = profile?.role === 'trainee';

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setIsLoading(true);
        
        const { data: gameData, error: gameError } = await supabase
          .from('linking_games')
          .select('*')
          .eq('file_id', fileId)
          .single();
          
        if (gameError) throw gameError;
        
        if (gameData) {
          setGameData(gameData);
          setPassingScore(gameData.passing_score);
          
          const { data: itemsData, error: itemsError } = await supabase
            .from('linking_items')
            .select('*')
            .eq('game_id', gameData.id)
            .order('id');
            
          if (itemsError) throw itemsError;
          
          setItems(itemsData || []);
          
          // Fetch previous attempt if exists
          if (session?.user?.id) {
            const { data: attemptData, error: attemptError } = await supabase
              .from('linking_attempts')
              .select('*')
              .eq('trainee_id', session.user.id)
              .eq('file_id', fileId)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
              
            if (!attemptError && attemptData && attemptData.passed) {
              setAttemptResult({
                score: attemptData.score || 0,
                passed: true,
                completed: true
              });
              
              if (attemptData.responses) {
                const savedSelections: {[key: string]: string} = {};
                const savedResults: {[key: string]: boolean} = {};
                
                // Fix: Check if responses is an array before calling forEach
                if (Array.isArray(attemptData.responses)) {
                  attemptData.responses.forEach((response: any) => {
                    if (response.itemId && response.answer) {
                      savedSelections[response.itemId] = response.answer;
                      
                      // Find the item to check if answer is correct
                      const item = itemsData?.find(i => i.id === response.itemId);
                      if (item) {
                        savedResults[response.itemId] = item.right_text === response.answer;
                      }
                    }
                  });
                }
                
                setSelections(savedSelections);
                setResults(savedResults);
                setShowResults(true);
              }

              // Important: Also ensure the training_progress entry is created for passed attempts
              updateTrainingProgress(true);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching linking game data:', error);
        toast({
          title: "Error",
          description: "Failed to load linking game data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    if (fileId) {
      fetchGameData();
    }
  }, [fileId, toast, session?.user?.id]);

  // Helper function to update training progress
  const updateTrainingProgress = async (isPassed: boolean) => {
    if (!session?.user?.id || !isTrainee || isAdmin) return;

    try {
      const now = new Date().toISOString();
      
      // First check if there's an existing training progress entry
      const { data: existingProgress, error: progressError } = await supabase
        .from('training_progress')
        .select('id, completed')
        .eq('resource_id', resourceId)
        .eq('file_id', fileId)
        .eq('user_id', session.user.id)
        .maybeSingle();
      
      if (progressError) {
        console.error('Error checking training progress:', progressError);
        return;
      }
      
      // If passed, ensure the training_progress record is updated
      if (isPassed) {
        if (existingProgress?.id) {
          // Update existing record
          if (!existingProgress.completed) {
            const { error: updateError } = await supabase
              .from('training_progress')
              .update({
                completed: true,
                completed_at: now,
                updated_at: now
              })
              .eq('id', existingProgress.id);
              
            if (updateError) {
              console.error('Error updating training progress:', updateError);
            } else {
              console.log('Successfully updated training progress to completed');
            }
          }
        } else {
          // Create new record
          const { error: insertError } = await supabase
            .from('training_progress')
            .insert({
              resource_id: resourceId,
              file_id: fileId,
              user_id: session.user.id,
              completed: true,
              completed_at: now,
              updated_at: now
            });
            
          if (insertError) {
            console.error('Error creating training progress:', insertError);
          } else {
            console.log('Successfully created training progress as completed');
          }
        }
      }
    } catch (error) {
      console.error('Error in updateTrainingProgress:', error);
    }
  };

  const updatePassingScore = async (newScore: number) => {
    if (!gameData?.id || !isAdmin) return;
    
    try {
      setIsUpdatingScore(true);
      
      const { error } = await supabase
        .from('linking_games')
        .update({ passing_score: newScore })
        .eq('id', gameData.id);
        
      if (error) throw error;
      
      setPassingScore(newScore);
      
      toast({
        title: "Success",
        description: "Passing score updated successfully",
      });
      
    } catch (error) {
      console.error('Error updating passing score:', error);
      toast({
        title: "Error",
        description: "Failed to update passing score",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingScore(false);
    }
  };

  const handleSelect = (itemId: string, rightText: string) => {
    setSelections(prev => ({
      ...prev,
      [itemId]: rightText
    }));
    
    if (results[itemId] !== undefined) {
      const newResults = { ...results };
      delete newResults[itemId];
      setResults(newResults);
    }
  };

  const checkAnswer = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item || !selections[itemId]) return false;
    
    return item.right_text === selections[itemId];
  };

  const checkAnswers = () => {
    const newResults: {[key: string]: boolean} = {};
    
    items.forEach(item => {
      if (selections[item.id]) {
        newResults[item.id] = checkAnswer(item.id);
      }
    });
    
    setResults(newResults);
    setShowResults(true);
    
    return newResults;
  };

  const handleSubmit = async () => {
    if (!session?.user?.id || isAdmin) return;
    
    try {
      setIsSubmitting(true);
      const checkedResults = checkAnswers();
      
      const totalItems = items.length;
      const correctCount = Object.values(checkedResults).filter(Boolean).length;
      const scorePercentage = Math.round((correctCount / totalItems) * 100);
      const isPassed = scorePercentage >= passingScore;
      
      console.log(`Score calculation: ${correctCount} correct out of ${totalItems} = ${scorePercentage}%`);
      
      const responsesData = Object.entries(selections).map(([itemId, answer]) => ({
        itemId,
        answer
      }));
      
      const { error: attemptError } = await supabase
        .from('linking_attempts')
        .insert({
          file_id: fileId,
          resource_id: resourceId,
          trainee_id: session.user.id,
          score: scorePercentage,
          passed: isPassed,
          completed_at: isPassed ? new Date().toISOString() : null,
          responses: responsesData
        });
      
      if (attemptError) throw attemptError;
      
      setAttemptResult({
        score: scorePercentage,
        passed: isPassed,
        completed: isPassed
      });
      
      if (isPassed) {
        // Update training progress to mark this file as completed
        await updateTrainingProgress(true);
        
        try {
          await markAsCompleted();
        } catch (progressError) {
          console.error('Error updating training progress:', progressError);
        }
        
        toast({
          title: "Success!",
          description: `You scored ${scorePercentage}% and passed the exercise!`,
        });
      } else {
        toast({
          title: "Try Again",
          description: `You scored ${scorePercentage}%. You need ${passingScore}% to pass.`,
          variant: "destructive",
        });
      }
      
    } catch (error) {
      console.error('Error submitting answers:', error);
      toast({
        title: "Error",
        description: "Failed to submit your answers",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetSelections = () => {
    setSelections({});
    setResults({});
    setShowResults(false);
    setAttemptResult(null);
  };

  const allCorrect = items.length > 0 && items.every(item => results[item.id] === true);
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
        <span className="ml-3">Loading game...</span>
      </div>
    );
  }
  
  if (!gameData) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load linking game data</AlertDescription>
        </Alert>
      </div>
    );
  }
  
  // When the game is completed and passed, show completed view
  if (attemptResult?.completed && !isAdmin) {
    const totalItems = items.length;
    const correctCount = Object.values(results).filter(Boolean).length;
    const scorePercentage = Math.round((correctCount / totalItems) * 100);
    
    return (
      <ScrollArea className="h-[calc(100vh-100px)]">
        <div className="p-6">
          <div className="bg-white rounded-xl border border-green-500 p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-[#1A1F2C] mb-4">
              Exercise Completed!
            </h3>
            <p className="text-lg text-[#8E9196] mb-6">
              Great job! You've successfully completed this matching exercise.
            </p>
            <div className="inline-block px-4 py-2 bg-green-50 rounded-full text-green-600 font-medium mb-8">
              Score: {scorePercentage}%
            </div>
            
            <div className="space-y-4 mb-8">
              {items.map((item) => (
                <div key={item.id} className={`p-4 rounded-lg border text-left ${
                  results[item.id] ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                }`}>
                  <div className="flex items-start gap-3">
                    {results[item.id] ? (
                      <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                    ) : (
                      <X className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                    )}
                    <div className="w-full">
                      <div className="flex justify-between gap-4">
                        <div className="flex-1">
                          <p className="font-medium text-[#1A1F2C]">{item.left_text}</p>
                        </div>
                        <div className="w-10 flex justify-center items-center">
                          <Link className={results[item.id] ? "text-green-500" : "text-red-500"} size={20} />
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${results[item.id] ? 'text-green-700' : 'text-red-700'}`}>
                            {selections[item.id]}
                          </p>
                        </div>
                      </div>
                      {!results[item.id] && (
                        <div className="mt-2 pt-2 border-t border-red-200">
                          <p className="text-sm text-red-700">
                            <span className="font-medium">Correct answer:</span> {item.right_text}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ScrollArea>
    );
  }
  
  const rightOptions = items.map(item => item.right_text);
  
  return (
    <ScrollArea className="h-[calc(100vh-100px)]">
      <div className="p-6 pb-24 relative">
        <h2 className="text-xl font-semibold mb-2">{gameData.title || "Linking Exercise"}</h2>
        <p className="text-muted-foreground mb-4">
          {gameData.description || "Match each item on the left with its corresponding item on the right."}
        </p>
        
        <div className="mb-4 flex items-center justify-between">
          {isAdmin ? (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[#8B5CF6]">Passing score:</span>
              <Select
                value={passingScore?.toString()}
                onValueChange={(value) => updatePassingScore(parseInt(value))}
                disabled={isUpdatingScore}
              >
                <SelectTrigger className="h-8 w-20 text-xs bg-[#F1F0FB] border-[#E5DEFF] text-[#8B5CF6]">
                  <SelectValue placeholder="Score" />
                </SelectTrigger>
                <SelectContent>
                  {[50, 60, 70, 80, 90, 100].map((score) => (
                    <SelectItem key={score} value={score.toString()}>
                      {score}%
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-sm inline-block px-3 py-1 bg-[#F1F0FB] rounded-full text-[#8B5CF6]">
              Passing score: {passingScore}%
            </p>
          )}
        </div>
        
        {isAdmin ? (
          <div className="space-y-4 mb-6">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-4">
                <div className="flex-1 bg-white p-3 rounded-md shadow-sm border">
                  {item.left_text}
                </div>
                <div className="w-16 flex justify-center">
                  <Link className="text-green-500" size={24} />
                </div>
                <div className="flex-1 bg-green-50 p-3 rounded-md shadow-sm border border-green-200">
                  {item.right_text}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4 mb-6">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-4">
                <div className="flex-1 bg-white p-3 rounded-md shadow-sm border">
                  {item.left_text}
                </div>
                <div className="w-16 flex justify-center">
                  {results[item.id] === true && <Link className="text-green-500" size={24} />}
                  {results[item.id] === false && <X className="text-red-500" size={24} />}
                </div>
                <div className="flex-1">
                  <Select
                    value={selections[item.id] || ""}
                    onValueChange={(value) => handleSelect(item.id, value)}
                    disabled={isSubmitting || attemptResult?.completed}
                  >
                    <SelectTrigger 
                      className={`w-full ${
                        results[item.id] === true ? 'bg-green-50 border-green-200 text-green-800' : 
                        results[item.id] === false ? 'bg-red-50 border-red-200 text-red-800' : ''
                      }`}
                    >
                      <SelectValue placeholder="Select match..." />
                    </SelectTrigger>
                    <SelectContent>
                      {rightOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {results[item.id] === false && (
                    <p className="text-red-500 text-xs mt-1">Incorrect match</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {showResults && allCorrect && !isAdmin && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <Check className="h-4 w-4 text-green-500" />
            <AlertTitle className="text-green-800">Perfect!</AlertTitle>
            <AlertDescription className="text-green-700">
              You've correctly matched all items.
            </AlertDescription>
          </Alert>
        )}
        
        <div className="fixed bottom-0 left-0 right-0 border-t bg-background/80 backdrop-blur-sm p-4 flex justify-center">
          {isAdmin ? (
            <div className="w-full text-center py-2 px-4 bg-gray-100 rounded-md max-w-md">
              <span className="text-sm text-gray-500">Admin preview mode - showing correct answers</span>
            </div>
          ) : (
            <>
              {attemptResult?.completed ? (
                <Button 
                  onClick={onComplete}
                  className="w-full max-w-md bg-green-600 hover:bg-green-700"
                >
                  Continue
                  <Send className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <div className="flex gap-2 w-full max-w-md">
                  {showResults && (
                    <Button
                      variant="outline"
                      onClick={resetSelections}
                      className="flex-1"
                    >
                      Try Again
                    </Button>
                  )}
                  <Button 
                    onClick={handleSubmit}
                    disabled={isSubmitting || Object.keys(selections).length !== items.length || attemptResult?.completed}
                    className="flex-1 bg-[#8B5CF6] hover:bg-[#7C3AED]"
                  >
                    {isSubmitting ? "Checking..." : "Check Answers"}
                    <Check className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}
